import { ReadFactory } from './chunk-N4PZ7ZK7.mjs';
import { encodePacked, zeroAddress } from './chunk-REJBMHQG.mjs';
import { getAddress, keccak256, encodeAbiParameters } from './chunk-VUTIU6H4.mjs';
import { DAY_SECONDS, DEFAULT_PD_SLUGS } from './chunk-THEIM6DM.mjs';
import { DopplerBytecode, DERC20Bytecode } from './chunk-PLRD7H53.mjs';

// src/entities/factory/ReadWriteFactory.ts
var DEFAULT_INITIAL_VOTING_DELAY = 7200;
var DEFAULT_INITIAL_VOTING_PERIOD = 50400;
var DEFAULT_INITIAL_PROPOSAL_THRESHOLD = BigInt(0);
var FLAG_MASK = BigInt(16383);
var flags = BigInt(
  1 << 13 | // BEFORE_INITIALIZE_FLAG
  1 << 12 | // AFTER_INITIALIZE_FLAG
  1 << 11 | // BEFORE_ADD_LIQUIDITY_FLAG
  1 << 7 | // BEFORE_SWAP_FLAG
  1 << 6 | // AFTER_SWAP_FLAG
  1 << 5
  // BEFORE_DONATE_FLAG
);
var ReadWriteFactory = class extends ReadFactory {
  /**
   * Creates a new ReadWriteFactory instance
   * @param address - The address of the factory contract
   * @param drift - A Drift instance with read-write adapter capabilities
   */
  constructor(address, drift) {
    super(address, drift);
  }
  /**
   * Computes the CREATE2 address for a contract deployment
   * @param salt - The salt used for deployment
   * @param initCodeHash - Hash of the initialization code
   * @param deployer - Address of the deploying contract
   * @returns The computed contract address
   * @private
   */
  computeCreate2Address(salt, initCodeHash, deployer) {
    const encoded = encodePacked(
      ["bytes1", "address", "bytes32", "bytes32"],
      ["0xff", deployer, salt, initCodeHash]
    );
    return getAddress(`0x${keccak256(encoded).slice(-40)}`);
  }
  /**
   * Validates basic parameters for pool deployment
   * @param params - The deployment configuration to validate
   * @throws {Error} When validation fails
   * @private
   */
  validateBasicParams(params) {
    if (!params.name || !params.symbol) {
      throw new Error("Name and symbol are required");
    }
    if (params.totalSupply <= 0) {
      throw new Error("Total supply must be positive");
    }
    if (params.numTokensToSell <= 0) {
      throw new Error("Number of tokens to sell must be positive");
    }
    if (params.tickRange) {
      if (params.tickRange.startTick >= params.tickRange.endTick) {
        throw new Error("Invalid tick range");
      }
    }
    if (params.duration <= 0) {
      throw new Error("Duration must be positive");
    }
    if (params.epochLength <= 0) {
      throw new Error("Epoch length must be positive");
    }
    if (params.tickSpacing <= 0) {
      throw new Error("Tick spacing must be positive");
    }
  }
  /**
   * Computes optimal gamma parameter based on price range and time parameters
   * Gamma determines how much the price can move per epoch during the sale.
   *
   * @param startTick - Starting tick of the price range
   * @param endTick - Ending tick of the price range
   * @param durationDays - Duration of the sale in days
   * @param epochLength - Length of each epoch in seconds
   * @param tickSpacing - Tick spacing for the pool
   * @returns The optimal gamma value
   * @throws {Error} If computed gamma is not divisible by tick spacing
   * @private
   */
  computeOptimalGamma(startTick, endTick, durationDays, epochLength, tickSpacing) {
    const totalEpochs = durationDays * DAY_SECONDS / epochLength;
    const tickDelta = Math.abs(endTick - startTick);
    let gamma = Math.ceil(tickDelta / totalEpochs) * tickSpacing;
    gamma = Math.max(tickSpacing, gamma);
    if (gamma % tickSpacing !== 0) {
      throw new Error("Computed gamma must be divisible by tick spacing");
    }
    return gamma;
  }
  /**
   * Encodes token factory initialization data
   * @param tokenConfig - Basic token configuration (name, symbol, URI)
   * @param vestingConfig - Vesting schedule configuration
   * @returns Encoded data for token factory initialization
   * @private
   */
  encodeTokenFactoryData(tokenConfig, vestingConfig) {
    return encodeAbiParameters(
      [
        { type: "string" },
        { type: "string" },
        { type: "uint256" },
        { type: "uint256" },
        { type: "address[]" },
        { type: "uint256[]" },
        { type: "string" }
      ],
      [
        tokenConfig.name,
        tokenConfig.symbol,
        vestingConfig.yearlyMintRate,
        vestingConfig.vestingDuration,
        vestingConfig.recipients,
        vestingConfig.amounts,
        tokenConfig.tokenURI
      ]
    );
  }
  /**
   * Encodes custom LP liquidity migrator data
   * @param customLPConfig - Configuration for custom LP migration
   * @param customLPConfig.customLPWad - Amount of custom LP tokens
   * @param customLPConfig.customLPRecipient - Recipient of custom LP tokens
   * @param customLPConfig.lockupPeriod - Lockup period for the tokens
   * @returns Encoded migrator data
   */
  encodeCustomLPLiquidityMigratorData(customLPConfig) {
    return encodeAbiParameters(
      [{ type: "uint64" }, { type: "address" }, { type: "uint32" }],
      [
        customLPConfig.customLPWad,
        customLPConfig.customLPRecipient,
        customLPConfig.lockupPeriod
      ]
    );
  }
  /**
   * Builds complete configuration for creating a new Doppler pool
   *
   * This method:
   * 1. Validates all input parameters
   * 2. Converts price ranges to tick ranges if needed
   * 3. Computes optimal gamma if not provided
   * 4. Mines hook and token addresses with proper flags
   * 5. Encodes all factory data
   *
   * @param params - Pre-deployment configuration parameters
   * @param addresses - Addresses of required Doppler V4 contracts
   * @returns Object containing createParams, hook address, and token address
   *
   * @throws {Error} When validation fails or required parameters are missing
   *
   * @example
   * ```typescript
   * const config = factory.buildConfig({
   *   name: "Community Token",
   *   symbol: "COMM",
   *   totalSupply: parseEther("1000000"),
   *   numTokensToSell: parseEther("500000"),
   *   priceRange: { startPrice: 0.001, endPrice: 0.01 },
   *   duration: 30,
   *   epochLength: 3600,
   *   // ... other required parameters
   * }, addresses);
   * ```
   */
  buildConfig(params, addresses) {
    this.validateBasicParams(params);
    const gamma = params.gamma ?? this.computeOptimalGamma(
      params.tickRange.startTick,
      params.tickRange.endTick,
      params.duration,
      params.epochLength,
      params.tickSpacing
    );
    const startTime = params.blockTimestamp + 30;
    const endTime = params.blockTimestamp + params.duration * DAY_SECONDS + 30;
    const totalDuration = endTime - startTime;
    if (totalDuration % params.epochLength !== 0) {
      throw new Error("Epoch length must divide total duration evenly");
    }
    if (gamma % params.tickSpacing !== 0) {
      throw new Error("Computed gamma must be divisible by tick spacing");
    }
    const {
      tokenFactory,
      dopplerDeployer,
      v4Initializer,
      poolManager,
      airlock,
      migrator,
      governanceFactory
    } = addresses;
    const tokenParams = {
      name: params.name,
      symbol: params.symbol,
      initialSupply: params.totalSupply,
      airlock,
      yearlyMintRate: params.yearlyMintRate,
      vestingDuration: params.vestingDuration,
      recipients: params.recipients,
      amounts: params.amounts,
      tokenURI: params.tokenURI
    };
    const dopplerParams = {
      minimumProceeds: params.minProceeds,
      maximumProceeds: params.maxProceeds,
      startingTime: BigInt(startTime),
      endingTime: BigInt(endTime),
      startingTick: params.tickRange.startTick,
      endingTick: params.tickRange.endTick,
      epochLength: BigInt(params.epochLength),
      gamma,
      isToken0: false,
      numPDSlugs: BigInt(params.numPdSlugs ?? DEFAULT_PD_SLUGS),
      fee: params.fee,
      tickSpacing: params.tickSpacing
    };
    const numeraire = params.numeraire ?? zeroAddress;
    const [salt, hook, token, poolInitializerData, tokenFactoryData] = this.mineHookAddress({
      airlock,
      poolManager,
      deployer: dopplerDeployer,
      initialSupply: params.totalSupply,
      numTokensToSell: params.numTokensToSell,
      numeraire,
      tokenFactory,
      tokenFactoryData: tokenParams,
      poolInitializer: v4Initializer,
      poolInitializerData: dopplerParams
    });
    const governanceFactoryData = encodeAbiParameters(
      [
        { type: "string" },
        { type: "uint48" },
        { type: "uint32" },
        { type: "uint256" }
      ],
      [
        params.name,
        DEFAULT_INITIAL_VOTING_DELAY,
        DEFAULT_INITIAL_VOTING_PERIOD,
        DEFAULT_INITIAL_PROPOSAL_THRESHOLD
      ]
    );
    return {
      createParams: {
        initialSupply: params.totalSupply,
        numTokensToSell: params.numTokensToSell,
        numeraire,
        tokenFactory,
        tokenFactoryData,
        governanceFactory,
        governanceFactoryData,
        poolInitializer: v4Initializer,
        poolInitializerData,
        liquidityMigrator: migrator,
        liquidityMigratorData: params.liquidityMigratorData ?? "0x",
        integrator: params.integrator,
        salt
      },
      hook,
      token
    };
  }
  /**
   * Mines a salt and hook address with the appropriate flags
   *
   * This method iterates through possible salt values to find a combination that:
   * - Produces a hook address with required Doppler flags
   * - Maintains proper token ordering relative to numeraire
   * - Ensures deterministic deployment addresses
   *
   * @param params - Parameters for hook address mining
   * @returns Tuple of [salt, hook address, token address, pool data, token data]
   * @throws {Error} If no valid salt can be found within the search limit
   * @private
   */
  mineHookAddress(params) {
    const isToken0 = params.numeraire !== "0x0000000000000000000000000000000000000000";
    const {
      minimumProceeds,
      maximumProceeds,
      startingTime,
      endingTime,
      startingTick,
      endingTick,
      epochLength,
      gamma,
      numPDSlugs,
      fee,
      tickSpacing
    } = params.poolInitializerData;
    const poolInitializerData = encodeAbiParameters(
      [
        { type: "uint256" },
        { type: "uint256" },
        { type: "uint256" },
        { type: "uint256" },
        { type: "int24" },
        { type: "int24" },
        { type: "uint256" },
        { type: "int24" },
        { type: "bool" },
        { type: "uint256" },
        { type: "uint24" },
        { type: "int24" }
      ],
      [
        minimumProceeds,
        maximumProceeds,
        startingTime,
        endingTime,
        startingTick,
        endingTick,
        epochLength,
        gamma,
        isToken0,
        numPDSlugs,
        fee,
        tickSpacing
      ]
    );
    const { poolManager, numTokensToSell, poolInitializer } = params;
    const hookInitHashData = encodeAbiParameters(
      [
        { type: "address" },
        { type: "uint256" },
        { type: "uint256" },
        { type: "uint256" },
        { type: "uint256" },
        { type: "uint256" },
        { type: "int24" },
        { type: "int24" },
        { type: "uint256" },
        { type: "int24" },
        { type: "bool" },
        { type: "uint256" },
        { type: "address" },
        { type: "uint24" }
      ],
      [
        poolManager,
        numTokensToSell,
        minimumProceeds,
        maximumProceeds,
        startingTime,
        endingTime,
        startingTick,
        endingTick,
        epochLength,
        gamma,
        isToken0,
        numPDSlugs,
        poolInitializer,
        fee
      ]
    );
    const hookInitHash = keccak256(
      encodePacked(
        ["bytes", "bytes"],
        [DopplerBytecode, hookInitHashData]
      )
    );
    const {
      name,
      symbol,
      yearlyMintRate,
      vestingDuration,
      recipients,
      amounts,
      tokenURI
    } = params.tokenFactoryData;
    const tokenFactoryData = this.encodeTokenFactoryData(
      {
        name,
        symbol,
        tokenURI
      },
      {
        amounts,
        recipients,
        vestingDuration,
        yearlyMintRate
      }
    );
    const { airlock, initialSupply } = params;
    const initHashData = encodeAbiParameters(
      [
        { type: "string" },
        { type: "string" },
        { type: "uint256" },
        { type: "address" },
        { type: "address" },
        { type: "uint256" },
        { type: "uint256" },
        { type: "address[]" },
        { type: "uint256[]" },
        { type: "string" }
      ],
      [
        name,
        symbol,
        initialSupply,
        airlock,
        airlock,
        yearlyMintRate,
        vestingDuration,
        recipients,
        amounts,
        tokenURI
      ]
    );
    const tokenInitHash = keccak256(
      encodePacked(["bytes", "bytes"], [DERC20Bytecode, initHashData])
    );
    for (let salt = BigInt(0); salt < BigInt(1e6); salt++) {
      const saltBytes = `0x${salt.toString(16).padStart(64, "0")}`;
      const hook = this.computeCreate2Address(
        saltBytes,
        hookInitHash,
        params.deployer
      );
      const token = this.computeCreate2Address(
        saltBytes,
        tokenInitHash,
        params.tokenFactory
      );
      const hookBigInt = BigInt(hook);
      const tokenBigInt = BigInt(token);
      const numeraireBigInt = BigInt(params.numeraire);
      if ((hookBigInt & FLAG_MASK) === flags && (isToken0 && tokenBigInt < numeraireBigInt || !isToken0 && tokenBigInt > numeraireBigInt)) {
        return [saltBytes, hook, token, poolInitializerData, tokenFactoryData];
      }
    }
    throw new Error("AirlockMiner: could not find salt");
  }
  /**
   * Creates a new Doppler pool with token, hook, migrator, and governance
   *
   * This is the main method for deploying a complete Doppler ecosystem:
   * - Deploys the token contract with vesting schedules
   * - Deploys the hook contract for price discovery
   * - Initializes the Uniswap V4 pool
   * - Sets up governance contracts
   * - Configures liquidity migration
   *
   * @param params - Complete creation parameters from buildConfig()
   * @param options - Optional transaction options (gas, value, etc.)
   * @returns Promise resolving to the transaction hash
   *
   * @example
   * ```typescript
   * const { createParams } = factory.buildConfig(config, addresses);
   * const txHash = await factory.create(createParams, {
   *   gasLimit: 5000000n
   * });
   * ```
   */
  async create(params, options) {
    return this.airlock.write("create", { createData: params }, options);
  }
  /**
   * Simulates a pool creation transaction without executing it
   *
   * Useful for:
   * - Estimating gas costs
   * - Validating parameters before execution
   * - Getting return values from the creation
   * - Testing deployment configurations
   *
   * @param params - Complete creation parameters from buildConfig()
   * @returns Promise resolving to simulation results including gas estimates
   *
   * @example
   * ```typescript
   * const simulation = await factory.simulateCreate(createParams);
   * console.log(`Estimated gas: ${simulation.request.gas}`);
   * ```
   */
  async simulateCreate(params) {
    return this.airlock.simulateWrite("create", { createData: params });
  }
  /**
   * Migrates liquidity for an existing asset from the current pool to the migration pool
   *
   * This method triggers the migration process for assets that have completed their
   * price discovery phase. The migration moves liquidity from the Doppler hook pool
   * to a standard Uniswap V4 pool for ongoing trading.
   *
   * @param asset - The address of the asset token to migrate
   * @param options - Optional transaction options (gas, value, etc.)
   * @returns Promise resolving to the transaction hash
   *
   * @example
   * ```typescript
   * // Migrate liquidity after price discovery ends
   * const txHash = await factory.migrate(tokenAddress);
   * ```
   */
  async migrate(asset, options) {
    return this.airlock.write("migrate", { asset }, options);
  }
};

export { ReadWriteFactory };
//# sourceMappingURL=chunk-S6CV5IS6.mjs.map
//# sourceMappingURL=chunk-S6CV5IS6.mjs.map